import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { Amount } from '@lx/shared/data-access/types';
import { AllowOrder, Supplement } from '@lx/shared/data-access/course';
import { courseSupplementAnimation } from '@lx/ui/animations';
import { DateFormat } from '@lx/shared/i18n';
import { DatePipe, PricePipe } from '@lx/shared/ui/pipes';
import { getDateFromString, isDateWithinRange } from '@lx/shared/utils';
import { AvailabilityStatus } from './course-supplement-availability-status.enum';
import { OptionalPriceDurationPipe, SupplementDurationUnitPipe } from '../../../pipes';

@Component({
  standalone: true,
  selector: 'lx-course-supplement',
  templateUrl: './course-supplement.component.html',
  styleUrls: ['./course-supplement.component.scss'],
  animations: [courseSupplementAnimation],
  imports: [CommonModule, DatePipe, OptionalPriceDurationPipe, PricePipe, SupplementDurationUnitPipe],
})
export class CourseSupplementComponent implements OnInit, OnChanges {
  @Input() supplement: Supplement;
  @Input() quantity = 0;
  @Input() currency: string;
  @Input() showDescription = false;
  @Input() quoteStart: Date | undefined;
  @Input() quoteEnd: Date | undefined;
  @Input() selectedSupplementIds: string[] = [];
  @Output() quantityChange = new EventEmitter<number>();

  protected DateFormat = DateFormat;
  protected AvailabilityStatus = AvailabilityStatus;

  protected readonly supplementSignal = signal<Supplement | undefined>(undefined);
  protected readonly optionsExpanded = signal<boolean>(false);

  // Computed properties
  protected readonly optionalsExist = computed(() => {
    return (this.supplementSignal()?.optionals?.length ?? 0) > 0;
  });

  protected readonly firstOptional = computed(() => {
    const optionals = this.supplementSignal()?.optionals;
    return optionals?.[0];
  });

  protected readonly remainingOptionals = computed(() => {
    const optionals = this.supplementSignal()?.optionals;
    return optionals && optionals.length > 1 ? optionals.slice(1) : [];
  });

  protected readonly multipleOptionalsExist = computed(() => {
    return (this.supplementSignal()?.optionals?.length ?? 0) > 1;
  });

  protected readonly toggleOptionsLabel = computed(() => {
    return this.optionsExpanded() ? $localize`See less options` : $localize`See all options`;
  });

  protected availabilityStatus: AvailabilityStatus | undefined;
  protected isMultiSelect: boolean | undefined;
  protected supplementSelected: boolean | undefined;
  protected canSelectSupplement: boolean | undefined;
  protected expired = false;
  protected disabled = true;

  ngOnInit(): void {
    this.supplementSignal.set(this.supplement);
    this.updateAvailabilityAndSelection();
    this.isMultiSelect = this.supplement.allowOrder !== AllowOrder.SELECTABLE;
    this.updateSupplementState();
    this.updateIsExpired();
    this.updateDisabledState();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { supplement, quoteStart, quoteEnd, selectedSupplementIds } = changes;

    if (supplement) {
      this.supplementSignal.set(this.supplement);
    }

    if (quoteStart || quoteEnd) {
      this.updateAvailabilityAndSelection();
    }
    if (selectedSupplementIds && this.supplement) {
      const selected = this.selectedSupplementIds.includes(this.supplement.code);
      this.supplementSelected = selected;
      if (selected) {
        if (this.quantity === 0) {
          this.quantity = this.supplement.quantity > 0 ? this.supplement.quantity : this.getSupplementMinUnit();
        }
      } else {
        this.quantity = 0;
      }
    }
    this.updateSupplementState();
    this.updateIsExpired();
    this.updateDisabledState();
  }

  toggleSelected() {
    if (!this.canSelectSupplement || this.expired) {
      return;
    }
    if (this.quantity) {
      this.quantity = 0;
    } else {
      this.quantity = this.supplement.quantity > 0 ? this.supplement.quantity : 1;
    }
    this.updateSupplementState();
    this.quantityChange.emit(this.quantity);
  }

  addSupplement() {
    if (!this.canSelectSupplement || this.expired) {
      return;
    }
    if (this.quantity < this.supplement.maxUnit) {
      const increment = this.quantity === 0 ? this.getSupplementMinUnit() : 1;
      this.quantity += increment;
      this.updateSupplementState();
      this.quantityChange.emit(this.quantity);
    }
  }

  removeSupplement() {
    if (this.quantity > 0) {
      const supplementMinUnit = this.getSupplementMinUnit();
      const decrement = this.quantity === supplementMinUnit ? supplementMinUnit : 1;
      this.quantity -= decrement;
      this.updateSupplementState();
      this.quantityChange.emit(this.quantity);
    }
  }

  displayCurrency(): string {
    const priceList = this.supplement?.price ?? [];
    if (this.currency && priceList.some((price: Amount) => price?.currency === this.currency)) {
      return this.currency;
    }
    return priceList[0]?.currency ?? '';
  }

  protected toggleOptionsExpansion(): void {
    this.optionsExpanded.set(!this.optionsExpanded());
  }

  private updateAvailabilityAndSelection(): void {
    this.availabilityStatus = this.getAvailabilityStatus();
    this.canSelectSupplement = this.isAvailableForSelection();
  }

  private getAvailabilityStatus(): AvailabilityStatus {
    if (!this.supplement?.availableDates?.periods?.length || this.isFullyAvailable()) {
      return AvailabilityStatus.FULLY_AVAILABLE;
    }
    if (this.isPartiallyAvailable()) {
      return AvailabilityStatus.PARTIALLY_AVAILABLE;
    }
    return AvailabilityStatus.NOT_AVAILABLE;
  }

  private isAvailableForSelection(): boolean {
    return this.availabilityStatus === AvailabilityStatus.FULLY_AVAILABLE || this.availabilityStatus === AvailabilityStatus.PARTIALLY_AVAILABLE;
  }

  private updateSupplementState() {
    this.supplementSelected = this.quantity > 0;
  }

  private getSupplementMinUnit(): number {
    return this.supplement.minUnit || 1;
  }

  private isPartiallyAvailable(): boolean {
    if (this.isFullyAvailable() || !this.supplement?.availableDates) {
      return false;
    }

    return this.supplement.availableDates?.periods?.some((period) => {
      const periodStart = getDateFromString(period.from);
      const periodEnd = getDateFromString(period.until);
      const supplementRange: [Date, Date] = [periodStart, periodEnd];
      const quoteRange: [Date, Date] = [this.quoteStart, this.quoteEnd];

      // Check if quote range overlaps with supplement range
      const isQuoteStartWithin = isDateWithinRange(this.quoteStart, supplementRange);
      const isQuoteEndWithin = isDateWithinRange(this.quoteEnd, supplementRange);

      // Check if supplement fully contained in quote range
      const isSupplementInQuoteRange = isDateWithinRange(periodStart, quoteRange) && isDateWithinRange(periodEnd, quoteRange);

      return isQuoteStartWithin || isQuoteEndWithin || isSupplementInQuoteRange;
    });
  }

  private isFullyAvailable(): boolean {
    return this.supplement?.availableDates?.periods?.some((period) => {
      const dateRange: [Date, Date] = [getDateFromString(period.from), getDateFromString(period.until)];
      return isDateWithinRange(this.quoteStart, dateRange) && isDateWithinRange(this.quoteEnd, dateRange);
    });
  }

  private updateIsExpired() {
    this.expired = !!this.supplement?.availableDates?.expired;
  }

  private updateDisabledState() {
    if (this.isMultiSelect) {
      this.disabled = !this.canSelectSupplement || (this.expired && !this.supplementSelected);
    } else {
      this.disabled = !this.canSelectSupplement || this.expired || this.quantity >= this.supplement.maxUnit;
    }
  }
}
